import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import axios, { API_ORIGIN } from "../axios";
import Logo from "../componant/ui/Logo";
import { getActiveTheme, getThemeSurface, loadThemeFont } from "../utils/themes";
import { coverUrl } from "../utils/cover";
import { groupLinks } from "../utils/linkImage";
import ImageLinkGrid from "../componant/ImageLinkGrid";
import LinkIcon from "../componant/ui/LinkIcon";
import { ArrowLeft, ArrowRight, Check, Copy, Monitor, Smartphone, Tablet } from "lucide-react";
   
const DEVICE_FRAMES = {
  mobile: {
    label: "Mobile",
    Icon: Smartphone,
    width: "380px",
    height: "760px",
    frame: "rounded-[2.5rem] border-[8px] border-slate-900",
  },
  tablet: {
    label: "Tablet",
    Icon: Tablet,
    width: "760px",
    height: "920px",
    frame: "rounded-[1.75rem] border-[10px] border-slate-900",
  },
  desktop: {
    label: "Desktop",
    Icon: Monitor,
    width: "100%",
    height: null,
    frame: "",
  },
};

const Show = (props) => {
  const { t } = useTranslation();
  let { user } = useParams();
  const [profile, setProfile] = useState();
  const [load, setload] = useState(true);
  const [viewMode, setViewMode] = useState("desktop");
  const [copied, setCopied] = useState(false);
  const [coverFailed, setCoverFailed] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    let uid = user
      ? user
      : props.id
        ? props.id
        : JSON.parse(localStorage.getItem("token")).user.username;
    axios
      .get("/user/" + uid)
      .then(({ data }) => {
        setProfile(data.data);
        setload(false);
      })
      .catch(() => {
        navigate("/en/home");
      });
  }, [props.id, user, navigate]);

  const checkIfKurdish = (val) => /[؀-ۿ]/.test(val);

  const getLinkHref = (element) => {
    const isPhone = element.icon.slice(0, 4) === "tell";
    // Phone links aren't clicks to an external site, so they skip the tracked redirect.
    if (isPhone && element.link.slice(0, 4) !== "http") return "tel:" + element.link;
    if (element.id != null) return `${API_ORIGIN}/r/${element.id}`;
    return element.link.slice(0, 4) === "http" ? element.link : "http://" + element.link;
  };

  const themeKey = profile?.settings?.theme;
  useEffect(() => {
    loadThemeFont(getActiveTheme(themeKey));
  }, [themeKey]);

  if (load) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-b from-indigo-50 to-white">
        <Logo animated="loader" className="w-24" title={t("Loading")} />
      </div>
    );
  }

  const activeTheme = getActiveTheme(profile?.settings?.theme);
  const themeSurface = getThemeSurface(activeTheme);
  const device = DEVICE_FRAMES[viewMode];
  // settings can be null for users without a settings row; a broken image falls back to the plain theme.
  const cover = coverUrl(profile?.settings);
  const showCover = Boolean(cover) && !coverFailed;

  // A visitor arriving via /:user gets the profile full-bleed with navigation;
  // the device-preview frame is only for the owner's own /en/Show view.
  const isPublicView = Boolean(user);

  const goBack = () => {
    if (window.history.state?.idx > 0) navigate(-1);
    else navigate("/en/home");
  };

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch {
      /* clipboard unavailable */
    }
  };

  const pillStyle = {
    background: activeTheme.isDark ? "rgba(255,255,255,0.16)" : "rgba(255,255,255,0.85)",
    border: `1px solid ${activeTheme.isDark ? "rgba(255,255,255,0.28)" : "#e2e8f0"}`,
    color: activeTheme.text,
    backdropFilter: "blur(8px)",
    WebkitBackdropFilter: "blur(8px)",
  };

  // Scrollbars are hidden but scrolling still works. The <style> exists only while this page is
  // mounted, so the page-level rule for the public view doesn't leak to other routes.
  const hideScrollbarCss = `
    .show-no-scrollbar { scrollbar-width: none; -ms-overflow-style: none; }
    .show-no-scrollbar::-webkit-scrollbar { display: none; }
    ${isPublicView ? "html { scrollbar-width: none; -ms-overflow-style: none; } html::-webkit-scrollbar { display: none; }" : ""}
  `;

  const profileCard = (
    <div
      className={
        "show-no-scrollbar relative w-full flex flex-col items-center px-4 transition-colors duration-500 " +
        (isPublicView ? "min-h-screen pt-24 pb-12" : "h-full overflow-y-auto py-12")
      }
      style={{ background: activeTheme.gradient, fontFamily: activeTheme.font }}
    >
      {showCover && (
        // Mask fades the image's alpha to 0, so the theme gradient shows through whatever its colors are.
        <div
          className="absolute top-0 inset-x-0 h-72 sm:h-96 overflow-hidden pointer-events-none"
          style={{
            WebkitMaskImage: "linear-gradient(to bottom, #000 0%, #000 35%, transparent 100%)",
            maskImage: "linear-gradient(to bottom, #000 0%, #000 35%, transparent 100%)",
          }}
        >
          <img
            src={cover}
            alt=""
            className="w-full h-full object-cover"
            onError={() => setCoverFailed(true)}
          />
        </div>
      )}
      <div className={"relative w-full max-w-md " + (showCover ? "mt-16" : "")}>
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-8">
          <div
            className="w-24 h-24 rounded-full overflow-hidden shadow-lg"
            style={{
              border: `4px solid ${activeTheme.isDark ? "rgba(255,255,255,0.85)" : "#ffffff"}`,
            }}
          >
            <img
              src={
                profile?.image
                  ? `${API_ORIGIN}/profile/${profile.image}`
                  : `${process.env.PUBLIC_URL}/images/df.png`
              }
              alt={profile?.username}
              className="w-full h-full object-cover"
            />
          </div>

          <h1
            className="mt-4 text-xl font-bold"
            style={{ color: activeTheme.text }}
          >
            @{profile?.username}
          </h1>

          {profile?.bio && profile.bio !== "null" && profile.bio !== "undefined" && (
            <p
              className={
                "mt-2 text-sm max-w-xs " +
                (checkIfKurdish(profile.bio) ? "text-right" : "")
              }
              style={{ color: activeTheme.text, opacity: themeSurface.mutedOpacity }}
            >
              {profile.bio}
            </p>
          )}
        </div>

        {/* Links list */}
        <div className="flex flex-col gap-3">
          {groupLinks(profile?.links || []).map((block) =>
            block.kind === "images" ? (
              <ImageLinkGrid
                key={"images-" + block.entries[0].index}
                items={block.entries}
                themeSurface={themeSurface}
              />
            ) : (
              <a
                key={block.index}
                href={getLinkHref(block.item)}
                target="_blank"
                rel="noopener noreferrer"
                className="group flex items-center gap-4 px-4 py-3.5 hover:-translate-y-0.5 transition-all duration-200"
                style={themeSurface.card}
              >
                <span
                  className="flex items-center justify-center w-10 h-10 shrink-0 transition-colors"
                  style={themeSurface.iconWrap}
                >
                  <LinkIcon
                    value={block.item.icon}
                    color={themeSurface.iconColor}
                    className="text-lg sm:text-xl"
                  />
                </span>
                <span
                  className="font-medium text-sm flex-1"
                  style={{ color: activeTheme.text }}
                >
                  {block.item.social_name}
                </span>
                <ArrowRight
                  className="w-4 h-4 shrink-0 transition-colors"
                  style={{ color: activeTheme.text, opacity: themeSurface.mutedOpacity }}
                  aria-hidden="true"
                />
              </a>
            )
          )}
        </div>

        {profile?.links?.length === 0 && (
          <p
            className="text-center text-sm"
            style={{ color: activeTheme.text, opacity: themeSurface.mutedOpacity }}
          >
            {t("No links yet.")}
          </p>
        )}

        {/* Footer */}
        <a
          href="/en/home"
          className="block text-center text-xs mt-10 hover:underline"
          style={{ color: activeTheme.text, opacity: themeSurface.mutedOpacity * 0.7 }}
        >
          <Logo className="w-5 inline-block align-middle mr-1.5 text-current" title="" />
          {t("Powered by Linkakam")}
        </a>
      </div>
    </div>
  );

  if (isPublicView) {
    return (
      <div className="relative min-h-screen w-full">
        <style>{hideScrollbarCss}</style>
        <div className="fixed top-0 inset-x-0 z-10 flex items-center justify-between p-4">
          <button
            type="button"
            onClick={goBack}
            className="inline-flex items-center gap-2 h-11 px-4 rounded-full text-sm font-semibold shadow-sm transition hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            style={pillStyle}
          >
            <ArrowLeft className="w-4 h-4" aria-hidden="true" />
            {t("Back")}
          </button>
          <button
            type="button"
            onClick={copyLink}
            aria-label={t("Copy profile link")}
            className="inline-flex items-center gap-2 h-11 px-4 rounded-full text-sm font-semibold shadow-sm transition hover:scale-[1.03] focus:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500"
            style={pillStyle}
          >
            {copied ? (
              <Check className="w-4 h-4" aria-hidden="true" />
            ) : (
              <Copy className="w-4 h-4" aria-hidden="true" />
            )}
            {copied ? t("Copied") : t("Copy link")}
          </button>
        </div>
        {profileCard}
      </div>
    );
  }

  return (
    <div className="min-h-screen w-full flex flex-col items-center bg-slate-100 py-6 lg:py-10 px-4">
      <style>{hideScrollbarCss}</style>
      {/* Device preview switcher - large screens only */}
      <div className="hidden lg:flex items-center gap-1.5 mb-6 bg-white rounded-full shadow-sm ring-1 ring-slate-200 p-1.5">
        {Object.entries(DEVICE_FRAMES).map(([key, cfg]) => (
          <button
            key={key}
            type="button"
            onClick={() => setViewMode(key)}
            aria-label={t(cfg.label)}
            aria-pressed={viewMode === key}
            title={t(cfg.label)}
            className={
              "w-10 h-10 rounded-full flex items-center justify-center transition " +
              (viewMode === key
                ? "bg-indigo-600 text-white shadow"
                : "text-slate-400 hover:text-indigo-600 hover:bg-indigo-50")
            }
          >
            <cfg.Icon className="w-5 h-5" aria-hidden="true" />
          </button>
        ))}
      </div>

      {viewMode === "desktop" ? (
        <div className="w-full min-h-[calc(100vh-96px)] rounded-2xl overflow-hidden shadow-sm">
          {profileCard}
        </div>
      ) : (
        <div
          className={
            "mx-auto overflow-hidden shadow-2xl transition-all duration-500 " + device.frame
          }
          style={{ width: device.width, height: device.height }}
        >
          {profileCard}
        </div>
      )}
    </div>
  );
};

export default Show;
